import { useState } from 'react';
import { Alert, Button, CircularProgress, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { startOfToday } from 'date-fns';
import { TextBlock } from '../../../Atoms/Text';
import { AdvisoryOpportunity } from '../../../../types/advise';
import {
  closeDateError,
  OPPORTUNITY_NAME_MAX_LENGTH,
  opportunityNameError,
  opportunityOptionLabel
} from './newEngagementForm';

export type OpportunityMode = 'new' | 'existing';

export interface OpportunityForm {
  mode: OpportunityMode;
  sfId: string;
  name: string;
  closeDate: Date | null;
}

export const EMPTY_OPPORTUNITY_FORM: OpportunityForm = { mode: 'new', sfId: '', name: '', closeDate: null };

export interface OpportunitySectionProps {
  form: OpportunityForm;
  onChange: (patch: Partial<OpportunityForm>) => void;
  loading: boolean;
  salesforceUserFound: boolean;
  opportunities: AdvisoryOpportunity[];
  newOpportunityStage: string;
  disabled: boolean;
}

export const OpportunitySection = ({
  form: { mode, sfId, name, closeDate },
  onChange,
  loading,
  salesforceUserFound,
  opportunities,
  newOpportunityStage,
  disabled
}: OpportunitySectionProps) => {
  // The date starts empty, so its error waits for the user to pick or clear one rather than greeting them.
  const [closeDateTouched, setCloseDateTouched] = useState(false);
  const nameError = opportunityNameError(name);
  const dateError = closeDateTouched ? closeDateError(closeDate) : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <CircularProgress size={16} />
        <TextBlock
          size="sm14"
          color="grey6">
          Loading your Opportunities…
        </TextBlock>
      </div>
    );
  }

  if (!salesforceUserFound) {
    return (
      <Alert
        severity="warning"
        data-testid="salesforce-user-not-found">
        Your Salesforce user could not be found. Contact an admin to link it before starting an engagement.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={mode}
        disabled={disabled}
        onChange={(_event, value: OpportunityMode | null) => value && onChange({ mode: value })}
        aria-label="Opportunity">
        <ToggleButton value="new">New Opportunity</ToggleButton>
        <ToggleButton value="existing">Existing Opportunity</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'existing' &&
        (opportunities.length === 0 ? (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                onClick={() => onChange({ mode: 'new' })}>
                New Opportunity
              </Button>
            }>
            No open Opportunities where you are the Subject Matter Expert on this Account
          </Alert>
        ) : (
          <TextField
            select
            required
            size="small"
            label="Select Opportunity"
            value={sfId}
            disabled={disabled}
            onChange={(event) => onChange({ sfId: event.target.value })}>
            {opportunities.map((opportunity) => (
              <MenuItem
                key={opportunity.sf_id}
                value={opportunity.sf_id}>
                {opportunityOptionLabel(opportunity)}
              </MenuItem>
            ))}
          </TextField>
        ))}

      {mode === 'new' && (
        <>
          <TextField
            required
            fullWidth
            size="small"
            label="Opportunity Name"
            value={name}
            disabled={disabled}
            error={!!nameError}
            helperText={nameError ?? `${name.length}/${OPPORTUNITY_NAME_MAX_LENGTH}`}
            onChange={(event) => onChange({ name: event.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              label="Close Date"
              value={closeDate}
              minDate={startOfToday()}
              disabled={disabled}
              onChange={(date) => {
                setCloseDateTouched(true);
                onChange({ closeDate: date });
              }}
              slotProps={{ textField: { required: true, size: 'small', error: !!dateError, helperText: dateError } }}
            />
            <TextField
              size="small"
              label="Stage"
              value={newOpportunityStage}
              disabled
            />
          </div>
        </>
      )}
    </div>
  );
};
